import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Calendar as CalendarComponent } from '../ui/calendar';
import { Label } from '../ui/label';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import LeaveOverrideDialog from '../LeaveOverrideDialog';
import { getLeaveBlock, withLeaveOverride } from '../../lib/leaveOverride';
import {
  AlertTriangle,
  Calendar,
  Clock,
  MapPin,
  Building2,
  Users,
  ArrowRight,
  MoreVertical,
  Package,
  Loader2,
  Search,
  ChevronsUpDown,
  FileText,
  Receipt,
  Plus,
  UserPlus,
  ChevronDown,
  ChevronRight,
  ArrowLeftRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parse } from 'date-fns';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const STATUS_LABELS = {
  booked:     { label: 'Booked',     cls: 'bg-zinc-500/15 text-zinc-300 border-zinc-500/40' },
  dispatched: { label: 'Dispatched', cls: 'bg-sky-500/15 text-sky-400 border-sky-500/40' },
  on_hire:    { label: 'On Hire',    cls: 'bg-[#9aff00]/15 text-[#9aff00] border-[#9aff00]/40' },
  off_hired:  { label: 'Off-Hired',  cls: 'bg-amber-500/15 text-amber-400 border-amber-500/40' },
  closed:     { label: 'Closed',     cls: 'bg-zinc-700/30 text-zinc-400 border-zinc-700/50' },
  cancelled:  { label: 'Cancelled',  cls: 'bg-red-500/15 text-red-400 border-red-500/40' },
};

const BILLING_LABELS = { weekly: 'Weekly', fortnightly: 'Fortnightly', monthly: 'Monthly' };

const TAB_CONFIG = [
  { key: 'active',    label: 'Active' },
  { key: 'booked',    label: 'Booked' },
  { key: 'on_hire',   label: 'On Hire' },
  { key: 'off_hired', label: 'Off-Hired' },
  { key: 'closed',    label: 'Closed' },
];

const fmtDate = (d) => {
  if (!d) return null;
  try { return format(new Date(d + 'T00:00:00'), 'EEE, dd MMM yyyy'); }
  catch { return d; }
};

export default function TowerCraneView({ api, onOpenJob, refreshKey = 0 }) {
  const [activeTab, setActiveTab] = useState('active');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState('start_date');
  const [sortDir, setSortDir] = useState('desc');

  // Daily docket dialog
  const [docketDialog, setDocketDialog] = useState({ open: false, job: null, template: null, loading: false });
  const [docketItems, setDocketItems] = useState([]);
  const _localToday = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
  const [docketDate, setDocketDate] = useState(_localToday());
  const [docketNotes, setDocketNotes] = useState('');
  const [savingDocket, setSavingDocket] = useState(false);

  // Invoice preview dialog
  const [invoiceDialog, setInvoiceDialog] = useState({ open: false, job: null, preview: null, loading: false });

  // Allocate workers dialog
  const [allocDialog, setAllocDialog] = useState({ open: false, job: null });
  const [allocWorkers, setAllocWorkers] = useState([]); // available workers list
  const [allocWorkerTypes, setAllocWorkerTypes] = useState([]); // worker types list
  const [allocForm, setAllocForm] = useState({ worker_id: '', worker_type_id: '', start_date: '', end_date: '', status: 'draft' });
  const [allocSaving, setAllocSaving] = useState(false);
  const [tcLeaveOverride, setTcLeaveOverride] = useState({ open: false, blocks: [], callback: null });
  const [historyOpen, setHistoryOpen] = useState(false);
  const [replaceTarget, setReplaceTarget] = useState(null); // { id, worker_type_id, end_date }
  const [replaceForm, setReplaceForm] = useState({ worker_id: '', start_date: '', end_date: '' });

  const fetchItems = async () => {
    setLoading(true);
    try {
      const statusParam = activeTab === 'active' ? 'active' : activeTab;
      const { data } = await api.get(`/tower-cranes?status=${statusParam}`);
      setItems(data?.items || []);
    } catch (e) {
      toast.error('Failed to load tower crane jobs');
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(); }, [activeTab, refreshKey]);

  // Load worker types on mount for card requirement badges
  useEffect(() => {
    api.get('/worker-types').then(res => setAllocWorkerTypes(res.data || [])).catch(() => {});
  }, []);

  // Fetch docket template for a job
  const handleOpenDocketDialog = async (job) => {
    setDocketDialog({ open: true, job, template: null, loading: true });
    setDocketDate(_localToday());
    setDocketNotes('');
    try {
      const { data } = await api.get(`/tower-cranes/${job.id}/docket-template?docket_date=${_localToday()}`);
      setDocketItems(data.items.map(item => ({ ...item })));
      setDocketDialog(prev => ({ ...prev, template: data, loading: false }));
      if (data.existing_docket) {
        toast.info(`A docket already exists for this date (${data.existing_docket.docket_number || data.existing_docket.id})`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load docket template');
      setDocketDialog({ open: false, job: null, template: null, loading: false });
    }
  };

  const handleSaveDocket = async () => {
    const job = docketDialog.job;
    if (!job) return;
    const filledItems = docketItems.filter(i => i.qty > 0);
    if (filledItems.length === 0) {
      toast.error('Enter hours for at least one worker');
      return;
    }
    setSavingDocket(true);
    try {
      await api.post('/dockets', {
        job_id: job.id,
        job_date: docketDate,
        customer_id: job.customer_id,
        site_id: job.site_id,
        equipment_type: job.equipment_type || 'Tower Crane',
        notes: docketNotes || null,
        items: filledItems,
      });
      toast.success('Daily docket created');
      setDocketDialog({ open: false, job: null, template: null, loading: false });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create docket');
    } finally {
      setSavingDocket(false);
    }
  };

  // Invoice preview
  const handleOpenInvoicePreview = async (job) => {
    setInvoiceDialog({ open: true, job, preview: null, loading: true });
    try {
      const { data } = await api.get(`/tower-cranes/${job.id}/invoice-preview`);
      setInvoiceDialog(prev => ({ ...prev, preview: data, loading: false }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load invoice preview');
      setInvoiceDialog({ open: false, job: null, preview: null, loading: false });
    }
  };

  // Allocate workers
  const handleOpenAllocDialog = async (job) => {
    setAllocDialog({ open: true, job });
    setAllocForm({ worker_id: '', worker_type_id: '', start_date: job.start_date || '', end_date: job.end_date || '', status: 'draft' });
    try {
      const [wRes, wtRes] = await Promise.all([
        api.get('/operators?is_active=true'),
        api.get('/worker-types'),
      ]);
      setAllocWorkers(wRes.data || []);
      setAllocWorkerTypes(wtRes.data || []);
    } catch (e) {
      toast.error('Failed to load workers');
    }
  };

  const handleAllocateWorker = async (leaveOverrideReason = null) => {
    const job = allocDialog.job;
    if (!job || !allocForm.worker_id || !allocForm.worker_type_id || !allocForm.start_date) return;
    setAllocSaving(true);
    try {
      const url = withLeaveOverride('/worker-allocations', leaveOverrideReason);
      await api.post(url, {
        job_id: job.id,
        operator_id: allocForm.worker_id,
        worker_type_id: allocForm.worker_type_id,
        start_date: allocForm.start_date,
        end_date: allocForm.end_date || allocForm.start_date,
        status: allocForm.status || 'draft',
      });
      toast.success('Worker allocated');
      setAllocForm(prev => ({ ...prev, worker_id: '', worker_type_id: '' }));
      fetchItems();
    } catch (e) {
      const lb = getLeaveBlock(e);
      if (lb && !leaveOverrideReason) {
        setTcLeaveOverride({
          open: true,
          blocks: [lb],
          callback: (reason) => handleAllocateWorker(reason),
        });
        return;
      }
      const detail = e.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map(d => d.msg || JSON.stringify(d)).join('; ') : 'Failed to allocate worker';
      toast.error(msg);
    } finally {
      setAllocSaving(false);
    }
  };

  const handleRemoveAllocation = async (allocationId) => {
    try {
      await api.delete(`/worker-allocations/${allocationId}`);
      toast.success('Worker removed');
      fetchItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to remove worker');
    }
  };

  const handleReplace = async (leaveOverrideReason = null) => {
    if (!replaceTarget || !replaceForm.worker_id || !replaceForm.start_date) return;
    setAllocSaving(true);
    try {
      const url = withLeaveOverride(`/worker-allocations/${replaceTarget.id}/replace`, leaveOverrideReason);
      await api.post(url, {
        new_operator_id: replaceForm.worker_id,
        new_start_date: replaceForm.start_date,
        new_end_date: replaceForm.end_date || undefined,
      });
      toast.success('Worker replaced');
      setReplaceTarget(null);
      setReplaceForm({ worker_id: '', start_date: '', end_date: '' });
      fetchItems();
    } catch (e) {
      const lb = getLeaveBlock(e);
      if (lb && !leaveOverrideReason) {
        setTcLeaveOverride({
          open: true,
          blocks: [lb],
          callback: (reason) => handleReplace(reason),
        });
        return;
      }
      const detail = e.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map(d => d.msg || JSON.stringify(d)).join('; ') : 'Failed to replace worker';
      toast.error(msg);
    } finally {
      setAllocSaving(false);
    }
  };

  // Keep the allocDialog's job in sync with the refreshed items list
  useEffect(() => {
    if (allocDialog.open && allocDialog.job) {
      const updated = items.find(j => j.id === allocDialog.job.id);
      if (updated) setAllocDialog(prev => ({ ...prev, job: updated }));
    }
  }, [items]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    let list = items;
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(j =>
        (j.customer_name || '').toLowerCase().includes(q) ||
        (j.site_name || '').toLowerCase().includes(q) ||
        (j.job_number || '').toLowerCase().includes(q) ||
        (j.asset_id || '').toLowerCase().includes(q)
      );
    }
    list = [...list].sort((a, b) => {
      const av = a[sortField] || '';
      const bv = b[sortField] || '';
      return sortDir === 'asc' ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
    });
    return list;
  }, [items, search, sortField, sortDir]);

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('desc'); }
  };

  return (
    <div className="space-y-4" data-testid="tower-crane-view">
      {/* Tab bar */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <TabsList className="h-9">
            {TAB_CONFIG.map(t => (
              <TabsTrigger key={t.key} value={t.key} className="text-xs px-3" data-testid={`tc-tab-${t.key}`}>
                {t.label}
              </TabsTrigger>
            ))}
          </TabsList>
          <div className="flex items-center gap-3">
            <div className="relative w-64">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search jobs..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-8 h-9 text-sm"
                data-testid="tc-search-input"
              />
            </div>
          </div>
        </div>
      </Tabs>

      {/* Sort toggles */}
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <span>Sort:</span>
        {[
          { key: 'start_date', label: 'Start Date' },
          { key: 'customer_name', label: 'Customer' },
          { key: 'days_on_hire', label: 'Days on Hire' },
        ].map(s => (
          <Button key={s.key} variant="ghost" size="sm" className={`h-6 px-2 text-xs ${sortField === s.key ? 'text-foreground font-medium' : ''}`} onClick={() => toggleSort(s.key)}>
            {s.label}
            {sortField === s.key && <ChevronsUpDown className="h-3 w-3 ml-1" />}
          </Button>
        ))}
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-12 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading tower crane jobs...
        </div>
      )}

      {/* Empty state */}
      {!loading && filtered.length === 0 && (
        <div className="text-center py-16 text-muted-foreground" data-testid="tc-empty-state">
          <Package className="h-10 w-10 mx-auto mb-3 opacity-40" />
          <p className="font-medium">No tower crane jobs found</p>
          <p className="text-sm mt-1">
            {search ? 'Try a different search term' : 'Create a dry-hire job and enable the Tower Crane toggle to get started'}
          </p>
        </div>
      )}

      {/* Job cards */}
      {!loading && filtered.length > 0 && (
        <div className="grid gap-3">
          {filtered.map(job => {
            const st = STATUS_LABELS[job.hire_status] || STATUS_LABELS.booked;
            const workerAllocs = job.worker_allocations || [];
            const workerReqs = job.worker_requirements || [];
            const billing = BILLING_LABELS[job.tower_crane_billing_period] || '—';

            // Build unfilled requirement indicators
            const unfilledReqs = workerReqs.flatMap(req => {
              const filledCount = workerAllocs.filter(wa => wa.worker_type_id === req.worker_type_id).length;
              const needed = (req.quantity || 1) - filledCount;
              if (needed <= 0) return [];
              const typeName = allocWorkerTypes.find(wt => wt.id === req.worker_type_id)?.name || req.worker_type_id;
              return Array.from({ length: needed }, (_, i) => ({ key: `${req.worker_type_id}-${i}`, name: typeName }));
            });
            return (
              <Card
                key={job.id}
                className="group hover:border-primary/40 transition-colors cursor-pointer"
                onClick={() => onOpenJob?.(job)}
                data-testid={`tc-job-card-${job.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    {/* Left */}
                    <div className="flex-1 min-w-0 space-y-2">
                      {/* Row 1: Job number + status + overdue */}
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-mono font-semibold text-foreground">{job.job_number}</span>
                        <Badge variant="outline" className={`text-[10px] ${st.cls}`}>{st.label}</Badge>
                        {job.is_overdue && (
                          <Badge variant="destructive" className="text-[10px] gap-1">
                            <AlertTriangle className="h-3 w-3" /> Overdue
                          </Badge>
                        )}
                        <Badge variant="outline" className="text-[10px] bg-amber-500/10 text-amber-400 border-amber-500/30">
                          Tower Crane
                        </Badge>
                      </div>

                      {/* Row 2: Customer + site */}
                      <div className="flex items-center gap-4 text-sm">
                        {job.customer_name && (
                          <span className="flex items-center gap-1 text-foreground">
                            <Building2 className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                            {job.customer_name}
                          </span>
                        )}
                        {(job.site_name || job.site_suburb) && (
                          <span className="flex items-center gap-1 text-muted-foreground">
                            <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                            {job.site_name || job.site_suburb}
                          </span>
                        )}
                      </div>

                      {/* Row 3: Asset + dates + billing */}
                      <div className="flex items-center gap-4 text-xs text-muted-foreground flex-wrap">
                        {job.asset_id && (
                          <span className="flex items-center gap-1">
                            <Package className="h-3 w-3" /> {job.asset_id}
                            {job.asset_capacity ? ` (${job.asset_capacity})` : ''}
                          </span>
                        )}
                        {job.start_date && (
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" /> {fmtDate(job.start_date)}
                            {job.expected_off_hire_date && <><ArrowRight className="h-3 w-3" /> {fmtDate(job.expected_off_hire_date)}</>}
                          </span>
                        )}
                        {job.days_on_hire != null && (
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" /> {job.days_on_hire}d on hire
                          </span>
                        )}
                        <span className="flex items-center gap-1 font-medium text-amber-400">
                          Billing: {billing}
                        </span>
                      </div>

                      {/* Row 4: Workers + unfilled requirements */}
                      {(workerAllocs.length > 0 || unfilledReqs.length > 0) && (
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <Users className="h-3.5 w-3.5 text-muted-foreground" />
                          {workerAllocs.slice(0, 5).map((wa, i) => (
                            <Badge key={wa.id || i} variant="secondary" className="text-[10px] py-0 h-5">
                              {wa.worker_name}
                            </Badge>
                          ))}
                          {workerAllocs.length > 5 && (
                            <span className="text-[10px] text-muted-foreground">+{workerAllocs.length - 5} more</span>
                          )}
                          {unfilledReqs.map(ur => (
                            <Badge key={ur.key} variant="outline" className="text-[10px] py-0 h-5 border-dashed border-orange-400/60 text-orange-400">
                              {ur.name} needed
                            </Badge>
                          ))}
                        </div>
                      )}
                      {workerAllocs.length === 0 && unfilledReqs.length === 0 && (
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground/60">
                          <Users className="h-3.5 w-3.5" />
                          <span>No workers allocated</span>
                        </div>
                      )}
                    </div>

                    {/* Right: actions */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity" onClick={e => e.stopPropagation()}>
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" onClick={e => e.stopPropagation()}>
                        <DropdownMenuItem onClick={() => onOpenJob?.(job)}>
                          Open Job
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => handleOpenDocketDialog(job)}>
                          <FileText className="h-4 w-4 mr-2" /> Create Daily Docket
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleOpenAllocDialog(job)}>
                          <UserPlus className="h-4 w-4 mr-2" /> Allocate Workers
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleOpenInvoicePreview(job)}>
                          <Receipt className="h-4 w-4 mr-2" /> Invoice Preview
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Summary footer */}
      {!loading && filtered.length > 0 && (
        <p className="text-xs text-muted-foreground text-right">
          {filtered.length} tower crane job{filtered.length !== 1 ? 's' : ''}
        </p>
      )}

      {/* ═══ Create Daily Docket Dialog ═══ */}
      <Dialog open={docketDialog.open} onOpenChange={(open) => { if (!open) setDocketDialog({ open: false, job: null, template: null, loading: false }); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Daily Docket — {docketDialog.job?.job_number}</DialogTitle>
            <DialogDescription>
              Enter hours worked by each allocated worker for this day.
            </DialogDescription>
          </DialogHeader>
          {docketDialog.loading ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading worker template...
            </div>
          ) : (
            <div className="space-y-4 py-2">
              <div>
                <Label>Docket Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-mono h-9 mt-1"
                      data-testid="tc-docket-date"
                    >
                      <Calendar className="mr-2 h-4 w-4" />
                      {docketDate ? format(parse(docketDate, 'yyyy-MM-dd', new Date()), 'EEE, dd MMM yyyy') : 'Select date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={docketDate ? parse(docketDate, 'yyyy-MM-dd', new Date()) : undefined}
                      onSelect={(date) => { if (date) setDocketDate(format(date, 'yyyy-MM-dd')); }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              {docketItems.length === 0 ? (
                <p className="text-sm text-muted-foreground">No workers allocated to this job. Allocate workers first.</p>
              ) : (
                <div className="space-y-2">
                  <Label>Workers</Label>
                  {docketItems.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-2 border rounded-md bg-muted/30" data-testid={`tc-docket-worker-${idx}`}>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{item.description}</p>
                        <p className="text-xs text-muted-foreground">{item.type_name}{item.unit_price ? ` · $${item.unit_price}/hr` : ''}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          min="0"
                          step="0.5"
                          className="w-20 text-right"
                          placeholder="hrs"
                          value={item.qty || ''}
                          onChange={e => {
                            const val = parseFloat(e.target.value) || 0;
                            setDocketItems(prev => prev.map((it, i) => i === idx ? { ...it, qty: val } : it));
                          }}
                          data-testid={`tc-docket-hours-${idx}`}
                        />
                        <span className="text-xs text-muted-foreground w-6">hrs</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div>
                <Label>Notes (optional)</Label>
                <Input value={docketNotes} onChange={e => setDocketNotes(e.target.value)} placeholder="Daily notes..." className="mt-1" data-testid="tc-docket-notes" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDocketDialog({ open: false, job: null, template: null, loading: false })}>Cancel</Button>
            <Button onClick={handleSaveDocket} disabled={savingDocket || docketItems.filter(i => i.qty > 0).length === 0} data-testid="tc-save-docket-btn">
              {savingDocket ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...</> : <><Plus className="h-4 w-4 mr-2" /> Create Docket</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ═══ Allocate Workers Dialog ═══ */}
      <Dialog open={allocDialog.open} onOpenChange={(open) => { if (!open) setAllocDialog({ open: false, job: null }); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Allocate Workers — {allocDialog.job?.job_number}</DialogTitle>
            <DialogDescription>Add or remove workers assigned to this tower crane job.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2 max-h-[70vh] overflow-y-auto">
            {/* Existing allocation blocks grouped by worker */}
            {(() => {
              const allocs = allocDialog.job?.worker_allocations || [];
              if (allocs.length === 0) return <p className="text-sm text-muted-foreground">No workers allocated yet.</p>;
              return (
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wide text-muted-foreground">Current Allocation Blocks</Label>
                  {allocs.map((wa, i) => {
                    const typeName = allocWorkerTypes.find(wt => wt.id === wa.worker_type_id)?.name || wa.worker_type_name || '—';
                    const startStr = wa.start_date ? format(parse(wa.start_date, 'yyyy-MM-dd', new Date()), 'dd MMM') : '—';
                    const endStr = wa.end_date ? format(parse(wa.end_date, 'yyyy-MM-dd', new Date()), 'dd MMM yyyy') : startStr;
                    return (
                      <div key={wa.id || i} className="flex items-center justify-between p-2.5 border rounded-md bg-muted/30" data-testid={`tc-alloc-block-${i}`}>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium truncate">{wa.worker_name || 'Unknown'}</p>
                            <Badge variant="outline" className="text-[10px] py-0 h-4 shrink-0">{typeName}</Badge>
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">{startStr} — {endStr}</p>
                        </div>
                        <div className="flex items-center gap-1 shrink-0 ml-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-blue-400 hover:text-blue-500 h-7 px-2"
                            onClick={() => {
                              setReplaceTarget({ id: wa.id, worker_type_id: wa.worker_type_id, end_date: wa.end_date });
                              setReplaceForm({ worker_id: '', start_date: '', end_date: wa.end_date || '' });
                            }}
                            data-testid={`tc-replace-alloc-${i}`}
                          >
                            <ArrowLeftRight className="h-3.5 w-3.5 mr-1" /> Replace
                          </Button>
                          <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-600 h-7 px-2" onClick={() => handleRemoveAllocation(wa.id)} data-testid={`tc-remove-alloc-${i}`}>
                            Remove
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}

            {/* ── Replace Worker Inline Form ── */}
            {replaceTarget && (
              <div className="border border-blue-500/40 rounded-lg p-3 space-y-3 bg-blue-500/5" data-testid="tc-replace-form">
                <div className="flex items-center justify-between">
                  <Label className="text-xs uppercase tracking-wide text-blue-400">Replace Worker</Label>
                  <Button variant="ghost" size="sm" className="h-6 px-2 text-xs text-muted-foreground" onClick={() => setReplaceTarget(null)}>Cancel</Button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Replacement Start Date <span className="text-red-500">*</span></Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left font-mono h-9 text-xs" data-testid="tc-replace-start-date">
                          <Calendar className="mr-2 h-3.5 w-3.5" />
                          {replaceForm.start_date ? format(parse(replaceForm.start_date, 'yyyy-MM-dd', new Date()), 'dd MMM yyyy') : 'Start date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <CalendarComponent
                          mode="single"
                          selected={replaceForm.start_date ? parse(replaceForm.start_date, 'yyyy-MM-dd', new Date()) : undefined}
                          onSelect={(date) => { if (date) setReplaceForm(prev => ({ ...prev, start_date: format(date, 'yyyy-MM-dd') })); }}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">End Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left font-mono h-9 text-xs" data-testid="tc-replace-end-date">
                          <Calendar className="mr-2 h-3.5 w-3.5" />
                          {replaceForm.end_date ? format(parse(replaceForm.end_date, 'yyyy-MM-dd', new Date()), 'dd MMM yyyy') : 'Same as original'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <CalendarComponent
                          mode="single"
                          selected={replaceForm.end_date ? parse(replaceForm.end_date, 'yyyy-MM-dd', new Date()) : undefined}
                          onSelect={(date) => { if (date) setReplaceForm(prev => ({ ...prev, end_date: format(date, 'yyyy-MM-dd') })); }}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <p className="text-[10px] text-muted-foreground">Defaults to original allocation end</p>
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Replacement Worker <span className="text-red-500">*</span></Label>
                  <Select value={replaceForm.worker_id} onValueChange={(v) => setReplaceForm(f => ({ ...f, worker_id: v }))}>
                    <SelectTrigger className="h-9" data-testid="tc-replace-worker-select">
                      <SelectValue placeholder="Select replacement worker..." />
                    </SelectTrigger>
                    <SelectContent>
                      {allocWorkers.map(w => (
                        <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  onClick={handleReplace}
                  disabled={allocSaving || !replaceForm.worker_id || !replaceForm.start_date}
                  className="w-full"
                  data-testid="tc-replace-confirm-btn"
                >
                  {allocSaving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Replacing...</> : <><ArrowLeftRight className="h-4 w-4 mr-2" /> Confirm Replacement</>}
                </Button>
              </div>
            )}

            {/* Unfilled requirement indicators */}
            {(() => {
              const reqs = allocDialog.job?.worker_requirements || [];
              const allocs = allocDialog.job?.worker_allocations || [];
              const unfilled = reqs.flatMap(req => {
                const filledCount = allocs.filter(wa => wa.worker_type_id === req.worker_type_id).length;
                const needed = (req.quantity || 1) - filledCount;
                if (needed <= 0) return [];
                const typeName = allocWorkerTypes.find(wt => wt.id === req.worker_type_id)?.name || req.worker_type_id;
                return [{ worker_type_id: req.worker_type_id, name: typeName, needed }];
              });
              if (unfilled.length === 0) return null;
              return (
                <div className="flex items-center gap-1.5 flex-wrap">
                  {unfilled.map(u => (
                    <Badge key={u.worker_type_id} variant="outline" className="text-[10px] py-0.5 border-dashed border-orange-400/60 text-orange-500">
                      {u.needed}x {u.name} needed
                    </Badge>
                  ))}
                </div>
              );
            })()}

            {/* Add allocation block form */}
            <div className="border-t pt-3 space-y-3">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Add Allocation Block</Label>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Start Date <span className="text-red-500">*</span></Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-left font-mono h-9 text-xs" data-testid="tc-alloc-start-date">
                        <Calendar className="mr-2 h-3.5 w-3.5" />
                        {allocForm.start_date ? format(parse(allocForm.start_date, 'yyyy-MM-dd', new Date()), 'dd MMM yyyy') : 'Start date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <CalendarComponent
                        mode="single"
                        selected={allocForm.start_date ? parse(allocForm.start_date, 'yyyy-MM-dd', new Date()) : undefined}
                        onSelect={(date) => { if (date) setAllocForm(prev => ({ ...prev, start_date: format(date, 'yyyy-MM-dd') })); }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">End Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-left font-mono h-9 text-xs" data-testid="tc-alloc-end-date">
                        <Calendar className="mr-2 h-3.5 w-3.5" />
                        {allocForm.end_date ? format(parse(allocForm.end_date, 'yyyy-MM-dd', new Date()), 'dd MMM yyyy') : 'End date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <CalendarComponent
                        mode="single"
                        selected={allocForm.end_date ? parse(allocForm.end_date, 'yyyy-MM-dd', new Date()) : undefined}
                        onSelect={(date) => { if (date) setAllocForm(prev => ({ ...prev, end_date: format(date, 'yyyy-MM-dd') })); }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  <p className="text-[10px] text-muted-foreground">Leave blank for single day</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Worker Type <span className="text-red-500">*</span></Label>
                  <Select value={allocForm.worker_type_id} onValueChange={(v) => setAllocForm(f => ({ ...f, worker_type_id: v }))}>
                    <SelectTrigger className="h-9" data-testid="tc-alloc-type-select">
                      <SelectValue placeholder="Select type..." />
                    </SelectTrigger>
                    <SelectContent>
                      {allocWorkerTypes.map(wt => (
                        <SelectItem key={wt.id} value={wt.id}>{wt.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Worker <span className="text-red-500">*</span></Label>
                  <Select value={allocForm.worker_id} onValueChange={(v) => setAllocForm(f => ({ ...f, worker_id: v }))}>
                    <SelectTrigger className="h-9" data-testid="tc-alloc-worker-select">
                      <SelectValue placeholder="Select worker..." />
                    </SelectTrigger>
                    <SelectContent>
                      {allocWorkers.map(w => (
                        <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button onClick={handleAllocateWorker} disabled={allocSaving || !allocForm.worker_id || !allocForm.worker_type_id || !allocForm.start_date} className="w-full" data-testid="tc-alloc-add-btn">
                {allocSaving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Adding...</> : <><UserPlus className="h-4 w-4 mr-2" /> Add Allocation Block</>}
              </Button>
            </div>

            {/* Allocation History (archived blocks) */}
            {(() => {
              const archived = allocDialog.job?.archived_worker_allocations || [];
              if (archived.length === 0) return null;
              return (
                <div className="border-t pt-3">
                  <button
                    type="button"
                    className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-muted-foreground hover:text-foreground transition-colors w-full"
                    onClick={() => setHistoryOpen(prev => !prev)}
                    data-testid="tc-alloc-history-toggle"
                  >
                    {historyOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                    Allocation History ({archived.length})
                  </button>
                  {historyOpen && (
                    <div className="space-y-2 mt-2">
                      {archived.map((wa, i) => {
                        const typeName = allocWorkerTypes.find(wt => wt.id === wa.worker_type_id)?.name || wa.worker_type_name || '—';
                        const startStr = wa.start_date ? format(parse(wa.start_date, 'yyyy-MM-dd', new Date()), 'dd MMM') : '—';
                        const endStr = wa.end_date ? format(parse(wa.end_date, 'yyyy-MM-dd', new Date()), 'dd MMM yyyy') : startStr;
                        const archivedDate = wa.archived_at ? format(new Date(wa.archived_at), 'dd MMM yyyy') : '';
                        return (
                          <div key={wa.id || i} className="p-2.5 border border-dashed rounded-md bg-muted/20 opacity-70" data-testid={`tc-alloc-history-${i}`}>
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-medium">{wa.worker_name || 'Unknown'}</p>
                              <Badge variant="outline" className="text-[10px] py-0 h-4">{typeName}</Badge>
                            </div>
                            <div className="flex items-center justify-between mt-0.5">
                              <p className="text-xs text-muted-foreground">{startStr} — {endStr}</p>
                              <p className="text-[10px] text-muted-foreground">Removed{archivedDate ? ` ${archivedDate}` : ''}{wa.archived_by ? ` by ${wa.archived_by}` : ''}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </DialogContent>
      </Dialog>

      {/* ═══ Invoice Preview Dialog ═══ */}
      <Dialog open={invoiceDialog.open} onOpenChange={(open) => { if (!open) setInvoiceDialog({ open: false, job: null, preview: null, loading: false }); }}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Invoice Preview — {invoiceDialog.job?.job_number}</DialogTitle>
            <DialogDescription>
              Combined equipment rental + labour charges for the current billing window.
            </DialogDescription>
          </DialogHeader>
          {invoiceDialog.loading ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Computing invoice...
            </div>
          ) : invoiceDialog.preview ? (
            <div className="space-y-4 py-2">
              {/* Window info */}
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <span>Window: {fmtDate(invoiceDialog.preview.window_start)} — {fmtDate(invoiceDialog.preview.window_end)}</span>
                <Badge variant="outline" className="text-xs">{invoiceDialog.preview.chargeable_days}d chargeable</Badge>
                <Badge variant="outline" className="text-xs">{BILLING_LABELS[invoiceDialog.preview.billing_period]}</Badge>
              </div>

              {/* Equipment lines */}
              <div>
                <h4 className="text-sm font-semibold mb-2 flex items-center gap-2"><Package className="h-4 w-4" /> Equipment Rental</h4>
                {invoiceDialog.preview.equipment_line_items?.length > 0 ? (
                  <div className="space-y-1">
                    {invoiceDialog.preview.equipment_line_items.map((li, i) => (
                      <div key={i} className="flex justify-between text-sm p-2 bg-muted/30 rounded">
                        <span>{li.description}</span>
                        <span className="font-mono">{li.qty} {li.uom} × ${li.rate?.toFixed(2)} = <b>${li.amount?.toFixed(2)}</b></span>
                      </div>
                    ))}
                    <div className="text-right text-sm font-semibold pt-1">Equipment subtotal: ${invoiceDialog.preview.equipment_subtotal?.toFixed(2)}</div>
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">No equipment charges (missing rates or no chargeable days)</p>
                )}
              </div>

              {/* Labour lines */}
              <div>
                <h4 className="text-sm font-semibold mb-2 flex items-center gap-2"><Users className="h-4 w-4" /> Labour ({invoiceDialog.preview.docket_count} docket{invoiceDialog.preview.docket_count !== 1 ? 's' : ''})</h4>
                {invoiceDialog.preview.labour_line_items?.length > 0 ? (
                  <div className="space-y-1">
                    {invoiceDialog.preview.labour_line_items.map((li, i) => (
                      <div key={i} className="flex justify-between text-sm p-2 bg-muted/30 rounded">
                        <span>{li.description}</span>
                        <span className="font-mono">{li.qty}h × {li.rate != null ? `$${li.rate.toFixed(2)}` : 'no rate'} = <b>${li.amount?.toFixed(2)}</b></span>
                      </div>
                    ))}
                    <div className="text-right text-sm font-semibold pt-1">Labour subtotal: ${invoiceDialog.preview.labour_subtotal?.toFixed(2)}</div>
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">No approved dockets in this billing window</p>
                )}
              </div>

              {/* Combined total */}
              <div className="border-t pt-3 flex justify-between items-center">
                <span className="text-sm font-semibold">Combined Total</span>
                <span className="text-lg font-bold">${invoiceDialog.preview.combined_subtotal?.toFixed(2)}</span>
              </div>

              {/* Warnings */}
              {invoiceDialog.preview.warnings?.length > 0 && (
                <div className="space-y-1">
                  {invoiceDialog.preview.warnings.map((w, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-amber-500">
                      <AlertTriangle className="h-3 w-3" /> {w}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setInvoiceDialog({ open: false, job: null, preview: null, loading: false })}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <LeaveOverrideDialog
        open={tcLeaveOverride.open}
        blocks={tcLeaveOverride.blocks}
        onCancel={() => setTcLeaveOverride({ open: false, blocks: [], callback: null })}
        onOverride={(reason) => {
          const cb = tcLeaveOverride.callback;
          setTcLeaveOverride({ open: false, blocks: [], callback: null });
          if (cb) cb(reason);
        }}
      />
    </div>
  );
}
